// ---------------------------------------------------------------------------
// V26 — THE RANKING, AND THE SENTENCE BESIDE EVERY LINE OF IT.
//
// `console.events.match`, at `events/:id/match` beside the board that will use it.
//
// ---------------------------------------------------------------------------
// THE EXPLANATION IS NOT A TOOLTIP
//
// Directive 2024/2831 does not ask for an explanation to be available; it asks
// for the decision to be explainable to the person it is about. So the sentence
// is on the row, in the reading order, at the same weight as the score — not
// behind a hover, not behind a chevron, and not abbreviated. The factor-by-factor
// breakdown is behind a control, because six sentences per candidate on a list of
// forty is a wall nobody reads; the ONE sentence is not.
//
// ---------------------------------------------------------------------------
// EXCLUDED PEOPLE ARE ON THE SCREEN
//
// The question this feature has to survive is *why was I not offered this*, and a
// list containing only the people who were offered it cannot answer that. So the
// excluded candidates are a second table, with the reason and the sentence, and
// they carry NO score — giving them a number would put them on the same scale as
// the ranked and turn a refusal into a poor showing.
//
// ---------------------------------------------------------------------------
// THE OVERRIDE IS ON EVERY ROW, IN EVERY STATE
//
// Including on the excluded rows, which is the state that matters: a person the
// machine refused is the person who most needs a human able to say otherwise. The
// control is hidden only from somebody who does not hold `match.override`, and
// the database asks the same question again.
//
// NO NEW CSS. Every class here already carries a rule in `web/src/design` —
// `stack`, `panel`, `data-table`, `field`, `btn`, `muted`, `numeric`. Run 42
// found twelve buttons across three slices spelling a class that did not exist.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { send } from "../api/auth";
import { console_ as consoleApi } from "../api/console";
import {
  match,
  type MatchCandidate,
  type MatchFactorLine,
  type MatchOverride,
  type MatchRun,
  type MatchVocabulary,
} from "../api/match";
import { Notice } from "../design/Notice";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { whenIn } from "../system/format";

type Post = { id: string; zone: string | null; role_name?: string; starts_at: string; ends_at: string };

export function Match() {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const tl = (text: string) => translateLabel(locale, text);
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const params = useParams();
  const eventId = params["id"] ?? "";

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [postId, setPostId] = useState<string>("");
  const [vocab, setVocab] = useState<MatchVocabulary | null>(null);
  const [vocabFailed, setVocabFailed] = useState(false);
  const [runs, setRuns] = useState<MatchRun[] | null>(null);
  const [mayRun, setMayRun] = useState(false);
  const [mayOverride, setMayOverride] = useState(false);
  const [runId, setRunId] = useState<string>("");
  const [candidates, setCandidates] = useState<MatchCandidate[] | null>(null);
  const [overrides, setOverrides] = useState<MatchOverride[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<MatchFactorLine[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ---- the posts on this event, and the vocabulary the screen renders with ----
  useEffect(() => {
    void (async () => {
      const res = await send<{ requirements?: Post[]; error?: { message: string } }>(
        consoleApi.listRequirements(apiBase, eventId),
      );
      // THE PAYLOAD IS AT THE TOP LEVEL. `ok()` wraps nothing, and V24 shipped
      // four screens that reached inside for a key the Worker does not produce.
      if (res.status === 200 && Array.isArray(res.body?.requirements)) {
        setPosts(res.body.requirements);
        if (res.body.requirements[0]) setPostId(res.body.requirements[0].id);
      } else {
        setFailed(refusal(res.body?.error) ?? t("The posts on this event could not be read."));
      }
    })();
  }, [apiBase, eventId]);

  useEffect(() => {
    void (async () => {
      const res = await send<MatchVocabulary & { error?: { message: string } }>(
        match.vocabulary(apiBase),
      );
      if (res.status === 200 && Array.isArray(res.body?.match_override_actions)) {
        setVocab(res.body);
        setVocabFailed(false);
      } else {
        // THE GALLERY FOUND THIS, AND IT IS THE WORST THING ON THE SCREEN.
        //
        // The first draft simply had no acts to render, so the override controls
        // vanished and the page looked completely normal — a tidy, working
        // ranking with its human review route silently absent. That is the state
        // Directive 2024/2831 makes illegal, and it was indistinguishable from a
        // scheduler who lacks the permission, from a run nobody has overridden,
        // and from everything working.
        //
        // An absence is something a bug produces by accident. A refusal is a fact.
        setVocabFailed(true);
      }
    })();
  }, [apiBase]);

  const loadRuns = useCallback(async () => {
    if (!postId) return;
    const res = await send<{
      runs?: MatchRun[];
      may_run?: boolean;
      may_override?: boolean;
      error?: { message: string };
    }>(match.runs(apiBase, postId));
    if (res.status === 200 && Array.isArray(res.body?.runs)) {
      setRuns(res.body.runs);
      setMayRun(res.body.may_run === true);
      setMayOverride(res.body.may_override === true);
      setRunId(res.body.runs[0]?.run_id ?? "");
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("The rankings for this post could not be read."));
    }
  }, [apiBase, postId]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const loadCandidates = useCallback(async () => {
    if (!runId) {
      setCandidates(null);
      setOverrides([]);
      return;
    }
    const res = await send<{
      candidates?: MatchCandidate[];
      overrides?: MatchOverride[];
      error?: { message: string };
    }>(match.candidates(apiBase, runId));
    if (res.status === 200 && Array.isArray(res.body?.candidates)) {
      setCandidates(res.body.candidates);
      setOverrides(res.body.overrides ?? []);
      setFailed(null);
    } else {
      setFailed(refusal(res.body?.error) ?? t("This ranking could not be read."));
    }
  }, [apiBase, runId]);

  useEffect(() => {
    void loadCandidates();
  }, [loadCandidates]);

  const rank = useCallback(async () => {
    setBusy(true);
    const res = await send<{ match_run?: { run_id: string }; error?: { message: string } }>(
      match.run(apiBase, postId),
    );
    setBusy(false);
    if (res.status >= 400 || !res.body?.match_run) {
      setFailed(refusal(res.body?.error) ?? t("That ranking could not be run."));
      return;
    }
    setSaid(t("Ranked. This is a record of an automated decision and cannot be edited."));
    await loadRuns();
  }, [apiBase, postId, loadRuns]);

  const showBreakdown = useCallback(
    async (candidateId: string) => {
      if (open === candidateId) {
        setOpen(null);
        return;
      }
      setOpen(candidateId);
      setBreakdown(null);
      const res = await send<{ breakdown?: MatchFactorLine[]; error?: { message: string } }>(
        match.breakdown(apiBase, candidateId),
      );
      if (res.status === 200 && Array.isArray(res.body?.breakdown)) setBreakdown(res.body.breakdown);
      else setFailed(refusal(res.body?.error) ?? t("That breakdown could not be read."));
    },
    [apiBase, open],
  );

  const override = useCallback(
    async (action: string, workerId: string | null) => {
      setBusy(true);
      const res = await send<{ error?: { message: string } }>(
        match.override(apiBase, runId, { action, worker_id: workerId }),
      );
      setBusy(false);
      if (res.status >= 400) {
        setFailed(refusal(res.body?.error) ?? t("That decision could not be recorded."));
        return;
      }
      setSaid(t("Recorded. The ranking is unchanged; what you decided sits beside it."));
      await loadCandidates();
    },
    [apiBase, runId, loadCandidates],
  );

  // Answer 64. The reader's own sort; it changes no rank and is never stored.
  const [byTravel, setByTravel] = useState(false);
  const rankedAsIs = (candidates ?? []).filter((c) => c.rank !== null);
  const ranked = byTravel
    ? [...rankedAsIs].sort((a, b) => (a.travel_minutes ?? Infinity) - (b.travel_minutes ?? Infinity))
    : rankedAsIs;
  const excluded = (candidates ?? []).filter((c) => c.rank === null);
  // THE ACTS COME FROM THE DATABASE. `needs_candidate` decides which control a row
  // offers, so a fourth act added next year appears here without this file moving.
  const rowActions = (vocab?.match_override_actions ?? []).filter((a) => a.needs_candidate);
  const runActions = (vocab?.match_override_actions ?? []).filter((a) => !a.needs_candidate);
  const current = runs?.find((r) => r.run_id === runId) ?? null;

  /**
   * What a person decided about this candidate, in the words the database holds.
   *
   * THE GALLERY FOUND THIS. The first draft drew a chip reading "A person decided"
   * for every override, so a candidate a human had REMOVED sat at rank 2 looking
   * like a top choice with a neutral badge beside them — the screen said a decision
   * had been taken and hid which one. `override_action` was on the row the whole
   * time; only the label was missing, and the label may not be spelled here
   * (absolute rule 4), so it comes from the vocabulary the server sent.
   *
   * The fallback is the bare id rather than a generic phrase: a word a reader does
   * not recognise sends them to ask, and "A person decided" sends them nowhere.
   */
  const decidedLabel = (actionId: string | null): string => {
    if (actionId === null) return "";
    const found = (vocab?.match_override_actions ?? []).find((a) => a.id === actionId);
    return found ? tl(found.label) : actionId;
  };

  return (
    <div className="stack">
      <h1>{t("Who to offer this to")}</h1>

      <section className="panel">
        <p className="muted">
          {t("The bench, ranked against one post. Every line says why, and nothing here decides anything on its own — the ranking is a suggestion a person acts on, and what you decide is recorded beside it whether it agrees or not.")}
        </p>
        {failed !== null ? <StatusChip tone="failed" label={failed} /> : null}
        {said !== null ? <StatusChip tone="live" label={said} /> : null}
        {vocabFailed ? (
          <Notice tone="failed">{t("The override controls could not be loaded, so nothing on this page can be overridden. Reload before you act on this ranking.")}</Notice>
        ) : null}

        <div className="field">
          <label htmlFor="v26-post">{t("Post")}</label>
          <select id="v26-post" value={postId} onChange={(e) => setPostId(e.target.value)}>
            {(posts ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.zone ?? (p.role_name ? translateLabel(locale, p.role_name) : t("This post"))} · {whenIn(p.starts_at, orgZone)}
              </option>
            ))}
          </select>
        </div>

        {posts !== null && posts.length === 0 ? (
          <p className="muted">{t("This event has no posts yet, so there is nothing to rank against.")}</p>
        ) : null}

        {mayRun ? (
          <button type="button" className="btn" disabled={busy || !postId} onClick={() => void rank()}>
            {t("Rank the bench against this post")}
          </button>
        ) : null}
      </section>

      {runs !== null && runs.length > 0 ? (
        <section className="panel">
          <div className="field">
            <label htmlFor="v26-run">{t("Ranking")}</label>
            <select id="v26-run" value={runId} onChange={(e) => setRunId(e.target.value)}>
              {runs.map((r) => (
                <option key={r.run_id} value={r.run_id}>
                  {whenIn(r.ranked_at, orgZone)} · {t("{ranked} ranked, {excluded} not", { ranked: r.ranked_count, excluded: r.excluded_count })}
                </option>
              ))}
            </select>
          </div>
          {current !== null ? (
            <p className="muted">
              A record of an automated decision. It cannot be edited — running the match again
              makes a new one — and it is kept until {whenIn(current.retain_until, orgZone)}.
            </p>
          ) : null}
          {mayOverride && runId
            ? runActions.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void override(a.id, null)}
                >
                  {tl(a.label)}
                </button>
              ))
            : null}
        </section>
      ) : null}

      {runs !== null && runs.length === 0 ? (
        <section className="panel">
          <p className="muted">
            {t("Nobody has run a match against this post yet.")}
          </p>
        </section>
      ) : null}

      {candidates !== null && ranked.length > 0 ? (
        <section className="panel">
          <h2>{t("Ranked")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th className="numeric">#</th>
                <th>{t("Who")}</th>
                <th className="numeric">{t("Score")}</th>
                <th className="numeric">
                  <button type="button" className="btn btn-quiet" aria-pressed={byTravel} onClick={() => setByTravel(!byTravel)}>
                    {t("Travel")}
                  </button>
                </th>
                <th>{t("Why")}</th>
                <th>{t("What a person decided")}</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((c) => (
                <tr key={c.candidate_id}>
                  <td className="numeric">{c.rank}</td>
                  <td>{c.worker_name}</td>
                  <td className="numeric">
                    {c.score}
                    {/* HOW MANY FACTORS THE SCORE IS AN AVERAGE OVER, ON THE ROW.
                        A 0.9 over four factors and a 0.9 over six are not the same
                        claim, and a number with no denominator invites a reader to
                        assume the larger one. */}
                    <br />
                    <span className="muted">{t("over {n}", { n: c.factors_measured })}</span>
                  </td>
                  <td className="numeric">
                    {c.travel_minutes == null ? t("Not known") : t("{m} min", { m: c.travel_minutes })}
                    {c.travel_minutes != null && c.max_travel_minutes != null && c.travel_minutes > c.max_travel_minutes ? (
                      <>
                        <br />
                        <span className="muted">{t("Further than they said they would travel")}</span>
                      </>
                    ) : null}
                  </td>
                  <td>
                    {c.explanation}
                    <br />
                    <button
                      type="button"
                      className="btn"
                      onClick={() => void showBreakdown(c.candidate_id)}
                    >
                      {open === c.candidate_id ? t("Hide the working") : t("Show the working")}
                    </button>
                    {open === c.candidate_id ? (
                      <ul>
                        {(breakdown ?? []).map((f) => (
                          <li key={f.factor_id}>
                            <strong>{tl(f.factor_label)}</strong>{" "}
                            {f.is_measurable ? (
                              <span className="numeric">{f.points}</span>
                            ) : (
                              <StatusChip tone="neutral" label={t("Not counted")} />
                            )}{" "}
                            {f.sentence}
                          </li>
                        ))}
                        {breakdown === null ? <li className="muted">{t("Reading the working…")}</li> : null}
                      </ul>
                    ) : null}
                  </td>
                  <td>
                    {c.overridden ? (
                      <StatusChip tone="attention" label={decidedLabel(c.override_action)} />
                    ) : null}
                    {mayOverride
                      ? rowActions.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className="btn"
                            disabled={busy}
                            onClick={() => void override(a.id, c.worker_id)}
                          >
                            {tl(a.label)}
                          </button>
                        ))
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {candidates !== null && excluded.length > 0 ? (
        <section className="panel">
          <h2>{t("Not on this list, and why")}</h2>
          <p className="muted">
            {t("Everybody the ranking left out, with the reason. They carry no score: being refused is not the same as scoring badly, and a number here would make it look like it was.")}
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Who")}</th>
                <th>{t("Why not")}</th>
                <th>{t("What a person decided")}</th>
              </tr>
            </thead>
            <tbody>
              {excluded.map((c) => (
                <tr key={c.candidate_id}>
                  <td>{c.worker_name}</td>
                  <td>
                    <StatusChip tone="attention" label={c.exclusion_label === null ? t("Not ranked") : tl(c.exclusion_label)} />{" "}
                    {c.explanation}
                  </td>
                  <td>
                    {c.overridden ? (
                      <StatusChip tone="live" label={decidedLabel(c.override_action)} />
                    ) : null}
                    {/* THE OVERRIDE IS HERE TOO, AND THIS IS THE ROW THAT MATTERS.
                        A human review route that only exists where the machine
                        agreed is not a human review route. */}
                    {mayOverride
                      ? rowActions.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className="btn"
                            disabled={busy}
                            onClick={() => void override(a.id, c.worker_id)}
                          >
                            {tl(a.label)}
                          </button>
                        ))
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {candidates !== null && candidates.length === 0 ? (
        <section className="panel">
          <p className="muted">
            {t("This ranking looked at nobody. There is no assignable person on the bench for this organisation yet.")}
          </p>
        </section>
      ) : null}

      {overrides.length > 0 ? (
        <section className="panel">
          <h2>{t("What people decided")}</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Who")}</th>
                <th>{t("What")}</th>
                <th>{t("Why")}</th>
                <th className="numeric">{t("When")}</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.override_id}>
                  <td>{o.worker_name ?? t("The whole ranking")}</td>
                  <td>{tl(o.action_label)}</td>
                  <td>{o.reason ?? "—"}</td>
                  <td className="numeric">{whenIn(o.decided_at, orgZone)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
